import numeral from 'numeral';
import Loading from '../../../components/Loading';
import Sparkline from '../../../components/Sparklines';
import { deltaPercent } from '../metrics';

/**
 * Every metric cell shows one of three things, decided by its own row's fetch: a loader while the
 * request is still out, a dash when that request failed, or the value. Each of the three cells a
 * metric occupies resolves this independently so they light up together as their row lands,
 * without any of them knowing about the other rows.
 */
function CellState({ status, error, metric, children }) {
    if (status === 'error') {
        return (
            <div className="error-message" title={error}>
                &mdash;
            </div>
        );
    }

    if (status !== 'done' || !metric) return <Loading span onlyPreloader />;

    return children(metric);
}

/** The metric's total (or ratio, for CTR) over the current period. */
export function MetricValue({ metric, status, error, format, csvDownload }) {
    if (csvDownload) return status === 'done' && metric ? metric.value : '';

    return (
        <CellState status={status} error={error} metric={metric}>
            {(loaded) => <strong>{numeral(loaded.value).format(format)}</strong>}
        </CellState>
    );
}

/** The metric's daily values across both periods — the full 60 days the report covers. */
export function MetricTrend({ metric, status, error, labels, keyName, format, csvDownload }) {
    if (csvDownload) return '-';

    return (
        <CellState status={status} error={error} metric={metric}>
            {(loaded) => (
                <Sparkline
                    dataL={loaded.series}
                    labels={labels}
                    keyName={keyName}
                    titleType={(date) => `Date: ${date}`}
                    customDataType={(value) => numeral(value).format(format)}
                />
            )}
        </CellState>
    );
}

/** The metric's change against the prior period, styled like the other GSC report tables. */
export function MetricDelta({ metric, status, error, csvDownload }) {
    const percent = deltaPercent(metric);

    if (csvDownload) return status === 'done' && percent !== null ? percent : '';

    return (
        <CellState status={status} error={error} metric={metric}>
            {() => {
                if (percent === null) {
                    return <div className="table-delta-number-arrow zero">&mdash;</div>;
                }

                if (!Number.isFinite(percent)) {
                    return (
                        <div className="table-delta-number-arrow up">
                            <i className="icon-arrow-right1"></i> &#8734;
                        </div>
                    );
                }

                const direction = percent === 0 ? 'zero' : percent > 0 ? 'up' : 'down';

                return (
                    <div className={`table-delta-number-arrow ${direction}`}>
                        {percent !== 0 && <i className="icon-arrow-right1"></i>}{' '}
                        {numeral(Math.abs(percent)).format('0.0')}%
                    </div>
                );
            }}
        </CellState>
    );
}
